import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import {
  FiPackage,
  FiCheckCircle,
  FiClock,
  FiTruck,
  FiMapPin,
  FiUser,
  FiPhone,
  FiCalendar,
} from "react-icons/fi";
import { ordersAPI } from "../../services/api";
import LoadingSpinner from "../LoadingSpinner";

const Container = styled.div`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 2rem;
`;

const Header = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 1.5rem;
  text-align: center;
`;

const TrackingNumber = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
  letter-spacing: 2px;
`;

const OrderId = styled.div`
  font-size: 0.9rem;
  opacity: 0.9;
`;

const Content = styled.div`
  padding: 2rem;
`;

const StatusSection = styled.div`
  margin-bottom: 2rem;
`;

const CurrentStatus = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: ${(props) => {
    switch (props.status) {
      case "delivered":
        return "#d4edda";
      case "out_for_delivery":
        return "#fff3cd";
      case "preparing":
        return "#cce5ff";
      case "confirmed":
        return "#e2e3e5";
      default:
        return "#f8d7da";
    }
  }};
  border-radius: 8px;
  border-left: 4px solid
    ${(props) => {
      switch (props.status) {
        case "delivered":
          return "#28a745";
        case "out_for_delivery":
          return "#ffc107";
        case "preparing":
          return "#007bff";
        case "confirmed":
          return "#6c757d";
        default:
          return "#dc3545";
      }
    }};
`;

const StatusIcon = styled.div`
  font-size: 2rem;
  margin-right: 1rem;
  color: ${(props) => {
    switch (props.status) {
      case "delivered":
        return "#28a745";
      case "out_for_delivery":
        return "#ffc107";
      case "preparing":
        return "#007bff";
      case "confirmed":
        return "#6c757d";
      default:
        return "#dc3545";
    }
  }};
`;

const StatusText = styled.div`
  font-size: 1.2rem;
  font-weight: 600;
  text-transform: capitalize;
`;

const Timeline = styled.div`
  position: relative;
  padding-left: 2rem;

  &::before {
    content: "";
    position: absolute;
    left: 1rem;
    top: 0;
    bottom: 0;
    width: 2px;
    background: #e9ecef;
  }
`;

const TimelineItem = styled(motion.div)`
  position: relative;
  margin-bottom: 2rem;

  &::before {
    content: "";
    position: absolute;
    left: -1.5rem;
    top: 0.5rem;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: ${(props) => (props.active ? "#007bff" : "#e9ecef")};
    border: 3px solid white;
    box-shadow: 0 0 0 2px ${(props) => (props.active ? "#007bff" : "#e9ecef")};
  }
`;

const TimelineContent = styled.div`
  background: ${(props) => (props.active ? "#f8f9fa" : "white")};
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid ${(props) => (props.active ? "#007bff" : "#e9ecef")};
`;

const TimelineHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const TimelineIcon = styled.div`
  font-size: 1.2rem;
  margin-right: 0.5rem;
  color: ${(props) => (props.active ? "#007bff" : "#6c757d")};
`;

const TimelineTitle = styled.div`
  font-weight: 600;
  color: ${(props) => (props.active ? "#007bff" : "#495057")};
`;

const TimelineTime = styled.div`
  font-size: 0.85rem;
  color: #6c757d;
  margin-left: auto;
`;

const TimelineNote = styled.div`
  font-size: 0.9rem;
  color: #6c757d;
  margin-top: 0.5rem;
`;

const OrderDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
`;

const DetailCard = styled.div`
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
  border: 1px solid #e9ecef;
`;

const DetailTitle = styled.h3`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  color: #495057;
  font-size: 1.1rem;
`;

const DetailIcon = styled.div`
  margin-right: 0.5rem;
  color: #007bff;
`;

const DetailContent = styled.div`
  color: #6c757d;
  line-height: 1.6;
`;

const DriverInfo = styled.div`
  display: flex;
  align-items: center;
  margin-top: 1rem;
  padding: 1rem;
  background: white;
  border-radius: 6px;
  border: 1px solid #e9ecef;
`;

const DriverAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #007bff;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  font-weight: bold;
`;

const DriverDetails = styled.div`
  flex: 1;
`;

const DriverName = styled.div`
  font-weight: 600;
  color: #495057;
`;

const DriverPhone = styled.div`
  font-size: 0.9rem;
  color: #6c757d;
  margin-top: 0.25rem;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: #dc3545;
`;

const OrderTracking = ({ trackingNumber, orderId }) => {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        setLoading(true);
        let response;

        if (trackingNumber) {
          response = await ordersAPI.trackOrder(trackingNumber);
        } else if (orderId) {
          response = await ordersAPI.getOrder(orderId);
        } else {
          throw new Error("No tracking number or order ID provided");
        }

        setOrder(response.data.data.order);
      } catch (err) {
        setError(
          err.response?.data?.message || "Failed to fetch order details"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [trackingNumber, orderId]);

  const getStatusIcon = (status) => {
    switch (status) {
      case "delivered":
        return <FiCheckCircle />;
      case "out_for_delivery":
        return <FiTruck />;
      case "preparing":
        return <FiPackage />;
      case "confirmed":
        return <FiCheckCircle />;
      default:
        return <FiClock />;
    }
  };

  const getStatusMessage = (status) => {
    switch (status) {
      case "delivered":
        return "Order Delivered";
      case "out_for_delivery":
        return "Out for Delivery";
      case "preparing":
        return "Preparing Your Order";
      case "confirmed":
        return "Order Confirmed";
      case "pending":
        return "Order Pending";
      case "cancelled":
        return "Order Cancelled";
      default:
        return "Unknown Status";
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <Container>
        <Content>
          <LoadingSpinner />
        </Content>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Content>
          <ErrorMessage>
            <h3>Unable to track order</h3>
            <p>{error}</p>
          </ErrorMessage>
        </Content>
      </Container>
    );
  }

  if (!order) {
    return (
      <Container>
        <Content>
          <ErrorMessage>
            <h3>Order not found</h3>
            <p>Please check your tracking number or order ID.</p>
          </ErrorMessage>
        </Content>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <TrackingNumber>
          {order.tracking_number || `#${order.id}`}
        </TrackingNumber>
        <OrderId>Order #{order.id}</OrderId>
      </Header>

      <Content>
        <StatusSection>
          <CurrentStatus status={order.status}>
            <StatusIcon status={order.status}>
              {getStatusIcon(order.status)}
            </StatusIcon>
            <StatusText>{getStatusMessage(order.status)}</StatusText>
          </CurrentStatus>

          {order.status_history && order.status_history.length > 0 && (
            <Timeline>
              {order.status_history.map((item, index) => (
                <TimelineItem
                  key={index}
                  active={index === order.status_history.length - 1}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <TimelineContent
                    active={index === order.status_history.length - 1}
                  >
                    <TimelineHeader>
                      <TimelineIcon
                        active={index === order.status_history.length - 1}
                      >
                        {getStatusIcon(item.status)}
                      </TimelineIcon>
                      <TimelineTitle
                        active={index === order.status_history.length - 1}
                      >
                        {getStatusMessage(item.status)}
                      </TimelineTitle>
                      <TimelineTime>{formatDate(item.created_at)}</TimelineTime>
                    </TimelineHeader>
                    {item.notes && <TimelineNote>{item.notes}</TimelineNote>}
                    {item.first_name && (
                      <TimelineNote>
                        Updated by: {item.first_name} {item.last_name}
                      </TimelineNote>
                    )}
                  </TimelineContent>
                </TimelineItem>
              ))}
            </Timeline>
          )}
        </StatusSection>

        <OrderDetails>
          <DetailCard>
            <DetailTitle>
              <DetailIcon>
                <FiPackage />
              </DetailIcon>
              Order Details
            </DetailTitle>
            <DetailContent>
              <div>
                <strong>Meal:</strong> {order.meal_name || order.standalone_meal_name || order.meal_plan_title || "N/A"}
              </div>
              {order.meal_plan_title && order.meal_name && (
                <div>
                  <strong>Meal Plan:</strong> {order.meal_plan_title}
                </div>
              )}
              <div>
                <strong>Vendor:</strong> {order.business_name}
              </div>
              <div>
                <strong>Order Date:</strong> {formatDate(order.order_date)}
              </div>
              <div>
                <strong>Delivery Date:</strong>{" "}
                {formatDate(order.delivery_date)}
              </div>
              {order.estimated_delivery_time && (
                <div>
                  <strong>Estimated Time:</strong>{" "}
                  {order.estimated_delivery_time}
                </div>
              )}
              {order.actual_delivery_time && (
                <div>
                  <strong>Delivered At:</strong>{" "}
                  {formatDate(order.actual_delivery_time)}
                </div>
              )}
            </DetailContent>
          </DetailCard>

          <DetailCard>
            <DetailTitle>
              <DetailIcon>
                <FiMapPin />
              </DetailIcon>
              Delivery Address
            </DetailTitle>
            <DetailContent>
              <div>{order.delivery_address}</div>
              {order.delivery_instructions && (
                <div style={{ marginTop: "0.5rem", fontStyle: "italic" }}>
                  <strong>Instructions:</strong> {order.delivery_instructions}
                </div>
              )}
            </DetailContent>
          </DetailCard>

          {order.driver_first_name && (
            <DetailCard>
              <DetailTitle>
                <DetailIcon>
                  <FiUser />
                </DetailIcon>
                Delivery Driver
              </DetailTitle>
              <DriverInfo>
                <DriverAvatar>
                  {order.driver_first_name[0]}
                  {order.driver_last_name[0]}
                </DriverAvatar>
                <DriverDetails>
                  <DriverName>
                    {order.driver_first_name} {order.driver_last_name}
                  </DriverName>
                  {order.driver_phone && (
                    <DriverPhone>
                      <FiPhone style={{ marginRight: "0.25rem" }} />
                      {order.driver_phone}
                    </DriverPhone>
                  )}
                </DriverDetails>
              </DriverInfo>
            </DetailCard>
          )}
        </OrderDetails>
      </Content>
    </Container>
  );
};

export default OrderTracking;
